import React, { createContext, ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';

import type {
    productOptions,
    productOptions_productOptions_items_ConfigurableProduct,
    productOptionsVariables,
} from 'src/queries/__generated__/productOptions';
import productOptionsQuery from 'src/queries/productOptions.graphql';
import { getUrlKeyForProduct } from 'src/util/getUrlKeyForProduct';
import { useDeps } from 'src/hooks/useDeps';
import { useRouterLocationCausesRender } from 'src/hooks/router-hooks';

import type { SelectedOptions, CleanConfigurableOptions, ConfigurableVariant } from './utils';
import { cleanConfigurableProduct, setInitialSelectedOptions } from './utils';

type ConfigurableOptionsContextType = {
    selectedOptions?: SelectedOptions;
    selectedVariant?: ConfigurableVariant;
    configurableOptions?: CleanConfigurableOptions;
    setSelectedVariant: (product: ConfigurableVariant) => void;
    isLoading: boolean;
    error?: ApolloError;
};

export const defaultContext: ConfigurableOptionsContextType = {
    selectedOptions: undefined,
    selectedVariant: undefined,
    setSelectedVariant: () => {
        console.warn("setSelectedSimple is not available on 'ConfigurableOptionsContext'");
    },
    configurableOptions: undefined,
    isLoading: false,
    error: undefined,
};

export const ConfigurableOptionsContext = createContext<ConfigurableOptionsContextType>(defaultContext);

type ConfigurableOptionsProviderProps = {
    pathname: string;
    children: ReactNode;
};

export const ConfigurableOptionsProvider = (props: ConfigurableOptionsProviderProps) => {
    const { search } = useRouterLocationCausesRender();

    const { loading, error, data } = useQuery<productOptions, productOptionsVariables>(productOptionsQuery, {
        variables: { urlKey: getUrlKeyForProduct(props.pathname) },
        ssr: useDeps().env.SSR_GQL,
    });

    if (error) {
        console.error(error);
    }

    const first = data?.productOptions?.items?.[0] as
        | productOptions_productOptions_items_ConfigurableProduct
        | undefined;

    const configurableOptions = cleanConfigurableProduct(first);

    const { selectedOptions, initialProduct } = setInitialSelectedOptions(
        configurableOptions?.configurable_options,
        configurableOptions?.variants,
        search,
    );

    const [selectedVariant, setSelectedVariant] = useState<ConfigurableVariant | undefined>(undefined);

    const api = useMemo(
        () => ({
            selectedOptions: selectedOptions,
            selectedVariant: selectedVariant ?? initialProduct,
            setSelectedVariant,
            configurableOptions,
            isLoading: loading,
            error,
        }),
        [selectedOptions, selectedVariant, initialProduct, configurableOptions, loading, error],
    );

    return <ConfigurableOptionsContext.Provider value={api}>{props.children}</ConfigurableOptionsContext.Provider>;
};
